import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { Separator } from './ui/separator';
import { Switch } from './ui/switch';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from './ui/dialog';
import {
  Users,
  Plus,
  LogIn,
  Copy,
  Share2,
  MapPin,
  Radio,
  Loader2,
  X,
  Crown,
  Clock,
  Trash2,
} from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const POLL_INTERVAL = 10000; // 10 seconds

const getInitials = (name) => {
  if (!name) return '?';
  return name
    .split(' ')
    .map((w) => w[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);
};

const formatTimeAgo = (timestamp) => {
  if (!timestamp) return '';
  const diff = Math.floor((Date.now() - new Date(timestamp).getTime()) / 1000);
  if (diff < 10) return 'gerade eben';
  if (diff < 60) return `vor ${diff}s`;
  if (diff < 3600) return `vor ${Math.floor(diff / 60)}m`;
  return `vor ${Math.floor(diff / 3600)}h`;
};

const ConvoyMode = ({ isOpen, onClose }) => {
  const [view, setView] = useState('lobby'); // 'lobby' | 'active'
  const [convoyId, setConvoyId] = useState(null);
  const [convoyName, setConvoyName] = useState('');
  const [joinCode, setJoinCode] = useState('');
  const [members, setMembers] = useState([]);
  const [isCreator, setIsCreator] = useState(false);
  const [sharingPosition, setSharingPosition] = useState(false);
  const [loading, setLoading] = useState(false);

  const [createName, setCreateName] = useState('');
  const [joinInput, setJoinInput] = useState('');

  const pollRef = useRef(null);
  const geoWatchRef = useRef(null);

  const pollPositions = useCallback(async () => {
    if (!convoyId) return;
    try {
      const response = await axios.get(`${API}/convoy/${convoyId}/positions`);
      const data = response.data;
      setMembers(Array.isArray(data) ? data : data.members || []);
    } catch (error) {
      console.error('Failed to poll positions:', error);
    }
  }, [convoyId]);

  // Poll positions when convoy is active
  useEffect(() => {
    if (view === 'active' && convoyId) {
      pollPositions();
      pollRef.current = setInterval(pollPositions, POLL_INTERVAL);
    }
    return () => {
      if (pollRef.current) {
        clearInterval(pollRef.current);
        pollRef.current = null;
      }
    };
  }, [view, convoyId, pollPositions]);

  // GPS tracking
  useEffect(() => {
    if (sharingPosition && convoyId) {
      const sendPosition = (position) => {
        axios
          .put(`${API}/convoy/${convoyId}/position`, {
            latitude: position.coords.latitude,
            longitude: position.coords.longitude,
          })
          .catch((err) => console.error('Failed to send position:', err));
      };

      if (navigator.geolocation) {
        geoWatchRef.current = navigator.geolocation.watchPosition(
          sendPosition,
          (err) => {
            console.error('Geolocation error:', err);
            toast.error('GPS-Zugriff fehlgeschlagen');
            setSharingPosition(false);
          },
          { enableHighAccuracy: true, maximumAge: 5000 }
        );
      } else {
        toast.error('GPS wird nicht unterst\u00FCtzt');
        setSharingPosition(false);
      }
    }

    return () => {
      if (geoWatchRef.current != null) {
        navigator.geolocation?.clearWatch(geoWatchRef.current);
        geoWatchRef.current = null;
      }
    };
  }, [sharingPosition, convoyId]);

  const handleCreate = async () => {
    if (!createName.trim()) {
      toast.error('Bitte einen Namen eingeben');
      return;
    }
    try {
      setLoading(true);
      const response = await axios.post(`${API}/convoy`, { name: createName.trim() });
      setConvoyId(response.data.id);
      setConvoyName(response.data.name || createName.trim());
      setJoinCode(response.data.join_code);
      setIsCreator(true);
      setView('active');
      toast.success('Konvoi erstellt');
    } catch (error) {
      console.error('Failed to create convoy:', error);
      toast.error('Konvoi konnte nicht erstellt werden');
    } finally {
      setLoading(false);
    }
  };

  const handleJoin = async () => {
    if (!joinInput.trim() || joinInput.trim().length < 6) {
      toast.error('Bitte einen g\u00FCltigen Code eingeben');
      return;
    }
    try {
      setLoading(true);
      const response = await axios.post(`${API}/convoy/join`, { code: joinInput.trim().toUpperCase() });
      setConvoyId(response.data.id);
      setConvoyName(response.data.name);
      setJoinCode(response.data.join_code || joinInput.trim().toUpperCase());
      setIsCreator(false);
      setView('active');
      toast.success('Konvoi beigetreten');
    } catch (error) {
      console.error('Failed to join convoy:', error);
      toast.error('Konvoi nicht gefunden');
    } finally {
      setLoading(false);
    }
  };

  const handleEnd = async () => {
    try {
      await axios.delete(`${API}/convoy/${convoyId}`);
      toast.success('Konvoi beendet');
    } catch (error) {
      console.error('Failed to end convoy:', error);
    }
    resetState();
  };

  const handleLeave = () => {
    setSharingPosition(false);
    resetState();
    toast.success('Konvoi verlassen');
  };

  const resetState = () => {
    setView('lobby');
    setConvoyId(null);
    setConvoyName('');
    setJoinCode('');
    setMembers([]);
    setIsCreator(false);
    setSharingPosition(false);
    setCreateName('');
    setJoinInput('');
  };

  const copyCode = () => {
    navigator.clipboard?.writeText(joinCode).then(
      () => toast.success('Code kopiert'),
      () => toast.error('Kopieren fehlgeschlagen')
    );
  };

  const shareCode = () => {
    if (navigator.share) {
      navigator.share({
        title: `Konvoi: ${convoyName}`,
        text: `Tritt meinem Konvoi bei! Code: ${joinCode}`,
      }).catch(() => {});
    } else {
      copyCode();
    }
  };

  const handleClose = () => {
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="bg-card border-border max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display text-xl text-foreground flex items-center gap-2">
            <Users className="w-5 h-5 text-primary" />
            Konvoi-Modus
          </DialogTitle>
          <DialogDescription>
            {view === 'lobby'
              ? 'Erstelle einen Konvoi oder tritt einem bei'
              : `${convoyName} \u2014 ${members.length} Teilnehmer`}
          </DialogDescription>
        </DialogHeader>

        {view === 'lobby' ? (
          <div className="space-y-6 mt-4">
            {/* Create Section */}
            <Card className="bg-muted/30 border-border">
              <CardHeader className="pb-3">
                <CardTitle className="text-base text-foreground flex items-center gap-2">
                  <Plus className="w-4 h-4 text-primary" />
                  Konvoi erstellen
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-2">
                  <Label className="text-sm text-foreground">Name</Label>
                  <Input
                    placeholder="z.B. Schwarzwald-Tour"
                    value={createName}
                    onChange={(e) => setCreateName(e.target.value)}
                    className="bg-muted/50 border-border h-10"
                    maxLength={50}
                  />
                </div>
                <Button
                  onClick={handleCreate}
                  disabled={loading || !createName.trim()}
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground active:scale-95 transition-all"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <Plus className="w-4 h-4 mr-2" />
                  )}
                  Erstellen
                </Button>
              </CardContent>
            </Card>

            <div className="flex items-center gap-3">
              <Separator className="flex-1 bg-border" />
              <span className="text-xs text-muted-foreground">oder</span>
              <Separator className="flex-1 bg-border" />
            </div>

            {/* Join Section */}
            <Card className="bg-muted/30 border-border">
              <CardHeader className="pb-3">
                <CardTitle className="text-base text-foreground flex items-center gap-2">
                  <LogIn className="w-4 h-4 text-primary" />
                  Konvoi beitreten
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <div className="space-y-2">
                  <Label className="text-sm text-foreground">Beitrittscode</Label>
                  <Input
                    placeholder="ABC123"
                    value={joinInput}
                    onChange={(e) => setJoinInput(e.target.value.toUpperCase())}
                    className="bg-muted/50 border-border h-10 font-mono text-center text-lg tracking-widest uppercase"
                    maxLength={6}
                  />
                </div>
                <Button
                  onClick={handleJoin}
                  disabled={loading || joinInput.trim().length < 6}
                  className="w-full bg-primary hover:bg-primary/90 text-primary-foreground active:scale-95 transition-all"
                >
                  {loading ? (
                    <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  ) : (
                    <LogIn className="w-4 h-4 mr-2" />
                  )}
                  Beitreten
                </Button>
              </CardContent>
            </Card>
          </div>
        ) : (
          <div className="space-y-5 mt-4">
            {/* Join Code Display */}
            <div className="text-center p-4 rounded-lg bg-muted/30 border border-border">
              <p className="text-xs text-muted-foreground mb-2">Beitrittscode</p>
              <div className="flex items-center justify-center gap-3">
                <span className="font-mono text-3xl font-bold text-primary tracking-[0.3em]">
                  {joinCode}
                </span>
              </div>
              <div className="flex gap-2 mt-3 justify-center">
                <Button
                  variant="outline"
                  size="sm"
                  className="border-border text-muted-foreground hover:text-foreground"
                  onClick={copyCode}
                >
                  <Copy className="w-3.5 h-3.5 mr-1.5" />
                  Kopieren
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="border-border text-muted-foreground hover:text-foreground"
                  onClick={shareCode}
                >
                  <Share2 className="w-3.5 h-3.5 mr-1.5" />
                  Teilen
                </Button>
              </div>
            </div>

            {/* Position Sharing Toggle */}
            <div className="flex items-center justify-between p-4 rounded-lg bg-muted/30 border border-border">
              <div className="flex items-center gap-3">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  sharingPosition ? 'bg-green-500/20' : 'bg-muted'
                }`}>
                  <Radio className={`w-4 h-4 ${sharingPosition ? 'text-green-500' : 'text-muted-foreground'}`} />
                </div>
                <div>
                  <p className="text-sm font-medium text-foreground">Position teilen</p>
                  <p className="text-xs text-muted-foreground">
                    {sharingPosition ? 'GPS aktiv' : 'GPS inaktiv'}
                  </p>
                </div>
              </div>
              <Switch
                checked={sharingPosition}
                onCheckedChange={setSharingPosition}
              />
            </div>

            <Separator className="bg-border" />

            {/* Members List */}
            <div>
              <h3 className="text-sm font-medium text-foreground mb-3 flex items-center gap-2">
                <Users className="w-4 h-4" />
                Teilnehmer ({members.length})
              </h3>
              <ScrollArea className="max-h-[240px]">
                <div className="space-y-2">
                  {members.length === 0 ? (
                    <p className="text-sm text-muted-foreground text-center py-6">
                      Warte auf Teilnehmer...
                    </p>
                  ) : (
                    members.map((member, i) => (
                      <div
                        key={member.id || i}
                        className="flex items-center gap-3 p-3 rounded-lg bg-muted/20 border border-border"
                      >
                        {/* Avatar */}
                        <div className="w-9 h-9 rounded-full bg-primary/20 text-primary flex items-center justify-center text-xs font-bold flex-shrink-0">
                          {getInitials(member.name)}
                        </div>

                        {/* Info */}
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-foreground truncate">
                              {member.name || 'Unbekannt'}
                            </span>
                            {member.is_creator && (
                              <Crown className="w-3.5 h-3.5 text-primary flex-shrink-0" />
                            )}
                          </div>
                          {member.last_update && (
                            <div className="flex items-center gap-1 text-xs text-muted-foreground">
                              <Clock className="w-3 h-3" />
                              {formatTimeAgo(member.last_update)}
                            </div>
                          )}
                        </div>

                        {/* Position indicator */}
                        {member.latitude && member.longitude && (
                          <MapPin className="w-4 h-4 text-green-500 flex-shrink-0" />
                        )}
                      </div>
                    ))
                  )}
                </div>
              </ScrollArea>
            </div>

            <Separator className="bg-border" />

            {/* Actions */}
            {isCreator ? (
              <Button
                variant="outline"
                className="w-full border-destructive/30 text-destructive hover:bg-destructive hover:text-destructive-foreground active:scale-95 transition-all"
                onClick={handleEnd}
              >
                <Trash2 className="w-4 h-4 mr-2" />
                Konvoi beenden
              </Button>
            ) : (
              <Button
                variant="outline"
                className="w-full border-border text-muted-foreground hover:text-foreground active:scale-95 transition-all"
                onClick={handleLeave}
              >
                <X className="w-4 h-4 mr-2" />
                Konvoi verlassen
              </Button>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ConvoyMode;
